import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography, 
  Grid,
  Card,
  CardContent,
  IconButton,
  Menu,
  MenuItem,
  ImageList,
  ImageListItem,
  Chip,
  Paper,
  Container
} from '@mui/material';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  LineChart,
  Line
} from 'recharts';
import { getAnalytics } from '../services/analyticsService';
import { useAuth } from '../context/AuthContext';
import { useCredits } from '../hooks/useCredits';
import LoadingScreen from '../components/common/LoadingScreen';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import PostAddIcon from '@mui/icons-material/PostAdd';
import SpeedIcon from '@mui/icons-material/Speed';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import PeopleIcon from '@mui/icons-material/People';
import ImageIcon from '@mui/icons-material/Image';
import axios from 'axios';

const COLORS = ['#5546ff', '#00c48c', '#ffa726', '#ef5350'];

const StatCard = ({ icon, title, value, color }) => (
  <Card sx={{ 
    height: '100%',
    background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(0,0,0,0.2)',
    boxShadow: '0 4px 16px 0 rgba(31, 38, 135, 0.03)'
  }}>
    <CardContent>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
        <Box sx={{ 
          p: 1, 
          borderRadius: 2, 
          bgcolor: `${color}15`,
          color: color,
          mr: 2
        }}>
          {icon}
        </Box>
        <Typography variant="subtitle2" color="text.secondary">
          {title}
        </Typography>
      </Box>
      <Typography variant="h4" sx={{ mb: 1, fontWeight: 600 }}>
        {value}
      </Typography>
    </CardContent>
  </Card>
);

const ChartCard = ({ title, children, noMenu = false }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  return (
    <Card sx={{ 
      height: '100%',
      background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(0,0,0,0.2)',
      boxShadow: '0 4px 16px 0 rgba(31, 38, 135, 0.03)'
    }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
          <Typography variant="h6">{title}</Typography>
          {!noMenu && (
            <IconButton size="small" onClick={(e) => setAnchorEl(e.currentTarget)}>
              <MoreVertIcon />
            </IconButton>
          )}
        </Box>
        {children}
      </CardContent>
      {!noMenu && (
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
        >
          <MenuItem>Export Data</MenuItem>
          <MenuItem>View Details</MenuItem>
        </Menu>
      )}
    </Card>
  );
};

const EmptyState = ({ message }) => (
  <Box sx={{ 
    height: '100%',
    minHeight: '300px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'text.secondary',
    border: '2px dashed',
    borderColor: 'divider',
    borderRadius: 2,
    p: 3,
    m: 0
  }}>
    <Typography 
      variant="body1"
      sx={{
        color: 'text.secondary',
        textAlign: 'center'
      }}
    >
      {message}
    </Typography>
  </Box>
);

const Analytics = () => {
  const { user, login } = useAuth();
  const { credits } = useCredits();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [userPlan, setUserPlan] = useState('free');

  useEffect(() => {
    const loadUserData = async () => {
      try {
        if (user?.id && (!user?.headline || !user?.google_id)) {
          const response = await axios.get(
            `${process.env.REACT_APP_API_URL}/api/auth/profile`,
            {
              headers: {
                Authorization: `Bearer ${localStorage.getItem('token')}`
              }
            }
          );
          
          const userData = response.data.user;
          if (userData) {
            login({ 
              ...user,
              ...userData,
              token: user.token 
            });
            // Store the updated user data in localStorage
            localStorage.setItem('user', JSON.stringify(userData));
            
            // Set the user plan
            if (userData.plan) {
              setUserPlan(userData.plan);
            }
          }
          setUserLoaded(true);
        } else if (user?.id) {
          // Set the user plan if available
          if (user.plan) {
            setUserPlan(user.plan);
          }
          setUserLoaded(true);
        }
      } catch (err) {
        console.error('Failed to load user data:', err);
        setUserLoaded(true);
      }
    };

    loadUserData();
  }, [user, login]);

  useEffect(() => {
    const fetchAnalytics = async () => {
      if (!user || !userLoaded) return;

      try {
        setLoading(true);
        const data = await getAnalytics();
        
        const contentDist = Array.isArray(data.contentDistribution) 
          ? data.contentDistribution.reduce((acc, item) => {
              acc[item.type] = item.count;
              return acc;
            }, {})
          : {};

        setAnalytics({
          ...data,
          contentDistribution: contentDist,
          postingSchedule: data.postingSchedule || []
        });
        setError(null);
      } catch (err) {
        console.error('Failed to fetch analytics:', err);
        setError('Failed to load analytics data');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalytics();
  }, [user, userLoaded]);

  if (!userLoaded || loading) return <LoadingScreen message="Loading analytics..." />;
  if (error) return <Container maxWidth="xl" className="page-container"><Box className="content-container"><Box sx={{ p: 3 }}><Typography color="error">{error}</Typography></Box></Box></Container>;
  if (!analytics) return null;

  const contentDistributionData = Object.entries(analytics?.contentDistribution || {}).map(([name, value]) => ({
    name: name.charAt(0).toUpperCase() + name.slice(1),
    value
  }));

  const postingScheduleData = (analytics?.postingSchedule || []).map(item => ({
    name: item.day,
    posts: item.count
  }));

  // Helper function to format numbers with commas
  const formatNumber = (num) => {
    return num ? num.toLocaleString() : '0';
  };
  
  // Helper function to determine if the plan has unlimited credits
  const hasUnlimitedCredits = () => {
    return ['business', 'enterprise', 'premium'].includes(userPlan.toLowerCase());
  };

  return (
    <>
      <Helmet>
        <title>LinkedIn Analytics Dashboard | Instation</title>
        <meta name="description" content="View comprehensive analytics and insights for your LinkedIn profile and content performance." />
      </Helmet>
      
      <Container maxWidth="xl" className="page-container">
        <Box className="content-container">
          <Grid container spacing={3}>
            <Grid item xs={12} className="content-panel">
              <Paper elevation={0} className="content-paper" sx={{ p: 3, borderRadius: 2, border: '1px solid rgba(0, 0, 0, 0.08)' }}>
                <Typography variant="h6" className="component-heading" sx={{ mb: 3 }}>
                  Instation Analytics
                </Typography>
                
                <Box sx={{ mb: 4, textAlign: 'center' }}>
                  <Typography variant="body1" color="text.secondary">
                    Overview of your content creation and platform usage
                  </Typography>
                </Box>
                
                <Grid container spacing={3}>
                  {/* Key Metrics */}
                  <Grid item xs={12}>
                    <ChartCard title="Key Metrics" 
                      sx={{ 
                        borderRadius: 2,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                        border: '1px solid rgba(0, 0, 0, 0.05)'
                      }}
                    >
                      <Box sx={{ p: 2 }}>
                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ 
                              p: 2, 
                              bgcolor: 'rgba(var(--primary-color-rgb), 0.1)',
                              borderRadius: 2,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              textAlign: 'center'
                            }}>
                              <Typography variant="h4" sx={{ fontWeight: 'bold', color: 'var(--primary-color)' }}>
                                {formatNumber(analytics?.totalPosts || 0)}
                              </Typography>
                              <Typography variant="subtitle2" sx={{ color: '#4A5568', fontWeight: 500 }}>
                                Total Posts
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ 
                              p: 2, 
                              bgcolor: 'rgba(56, 161, 105, 0.1)',
                              borderRadius: 2,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              textAlign: 'center'
                            }}>
                              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#38A169' }}>
                                {formatNumber(analytics?.totalImages || 0)}
                              </Typography>
                              <Typography variant="subtitle2" sx={{ color: '#4A5568', fontWeight: 500 }}>
                                Total Images
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ 
                              p: 2, 
                              bgcolor: 'rgba(237, 137, 54, 0.1)',
                              borderRadius: 2,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              textAlign: 'center'
                            }}>
                              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#ED8936' }}>
                                {hasUnlimitedCredits() ? '∞' : formatNumber(analytics?.totalCreditsUsed || 0)}
                              </Typography>
                              <Typography variant="subtitle2" sx={{ color: '#4A5568', fontWeight: 500 }}>
                                Credits Used
                              </Typography>
                            </Box>
                          </Grid>

                          <Grid item xs={12} sm={6} md={3}>
                            <Box sx={{ 
                              p: 2, 
                              bgcolor: 'rgba(159, 122, 234, 0.1)',
                              borderRadius: 2,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'center',
                              textAlign: 'center'
                            }}>
                              <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#9F7AEA' }}>
                                {hasUnlimitedCredits() ? '∞' : formatNumber(credits || 0)}
                              </Typography>
                              <Typography variant="subtitle2" sx={{ color: '#4A5568', fontWeight: 500 }}>
                                Available Credits
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </ChartCard>
                  </Grid>

                  {/* Recent Posts */}
                  <Grid item xs={12} md={6}>
                    <ChartCard 
                      title="Recent Posts" 
                      sx={{ 
                        height: '100%',
                        borderRadius: 2,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                        border: '1px solid rgba(0, 0, 0, 0.05)'
                      }}
                    >
                      <Box sx={{ mb: 2, px: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          Your most recently created posts
                        </Typography>
                      </Box>
                      {analytics.recentPosts && analytics.recentPosts.length > 0 ? (
                        <Box sx={{ maxHeight: 400, overflow: 'auto', px: 3 }}>
                            {analytics.recentPosts.map((post, index) => (
                              <Box 
                              key={post.id || index} 
                                sx={{ 
                                  p: 2, 
                                mb: 2, 
                                borderRadius: 2,
                                bgcolor: 'rgba(72, 144, 254, 0.03)',
                                border: '1px solid rgba(72, 144, 254, 0.1)',
                                '&:last-child': { mb: 0 },
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)'
                                }
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#2D3748', mb: 1 }}>
                                  {post.topic || 'Untitled Post'}
                                </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{ 
                                mb: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical'
                              }}>
                                  {post.content}
                                </Typography>
                              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="caption" color="text.secondary">
                                  {new Date(post.created_at).toLocaleDateString()} at {new Date(post.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                                <Chip 
                                  label={post.status || 'Draft'} 
                                  size="small"
                                  sx={{
                                    bgcolor: post.status === 'Published' ? 'rgba(56, 161, 105, 0.1)' : 'rgba(237, 137, 54, 0.1)',
                                    color: post.status === 'Published' ? '#38A169' : '#ED8936',
                                    fontWeight: 600,
                                    fontSize: '0.7rem'
                                  }}
                                />
                              </Box>
                              </Box>
                            ))}
                          </Box>
                        ) : (
                        <Box sx={{ 
                          p: 4, 
                          display: 'flex', 
                          flexDirection: 'column', 
                          alignItems: 'center', 
                          justifyContent: 'center',
                          color: 'text.secondary',
                          bgcolor: 'rgba(0, 0, 0, 0.02)',
                          borderRadius: 2,
                          mx: 3,
                          mb: 3
                        }}>
                          <PostAddIcon sx={{ fontSize: 40, color: '#A0AEC0', mb: 2 }} />
                          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                            No posts created yet. Start creating content to see your posts here.
                          </Typography>
                        </Box>
                        )}
                      </ChartCard>
                    </Grid>

                  {/* Recent Images */}
                  <Grid item xs={12} md={6}>
                    <ChartCard 
                      title="Recent Images" 
                      sx={{ 
                        height: '100%',
                        borderRadius: 2,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                        border: '1px solid rgba(0, 0, 0, 0.05)'
                      }}
                    >
                      <Box sx={{ mb: 2, px: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          Your most recently generated images
                        </Typography>
                      </Box>
                      {analytics.recentImages && analytics.recentImages.length > 0 ? (
                          <Box sx={{ 
                          px: 3,
                          pb: 3,
                            display: 'grid', 
                            gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))',
                          gap: 2,
                          maxHeight: 400,
                          overflow: 'auto'
                          }}>
                          {analytics.recentImages.map((image, index) => (
                              <Box 
                              key={image.id || index}
                                sx={{ 
                                  aspectRatio: '1',
                                borderRadius: 2,
                                  overflow: 'hidden',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                                transition: 'transform 0.2s, box-shadow 0.2s',
                                  '&:hover': {
                                transform: 'scale(1.03)',
                                boxShadow: '0 8px 20px rgba(0, 0, 0, 0.12)'
                              },
                              position: 'relative'
                              }}
                            >
                              <img 
                                src={image.image} 
                                alt={image.topic || 'Generated image'} 
                              style={{
                                width: '100%',
                                height: '100%',
                                objectFit: 'cover'
                              }}
                                loading="lazy"
                              />
                            <Box sx={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              right: 0,
                              p: 1,
                              background: 'linear-gradient(to top, rgba(0,0,0,0.7), rgba(0,0,0,0))',
                              color: 'white',
                              fontSize: '0.7rem',
                              fontWeight: 500,
                              opacity: 0,
                              transition: 'opacity 0.2s',
                              'box-root:hover &': {
                                opacity: 1
                              }
                            }}>
                              {new Date(image.created_at).toLocaleDateString()}
                            </Box>
                            </Box>
                          ))}
                        </Box>
                      ) : (
                      <Box sx={{ 
                        p: 4, 
                        display: 'flex', 
                        flexDirection: 'column', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        color: 'text.secondary',
                        bgcolor: 'rgba(0, 0, 0, 0.02)',
                        borderRadius: 2,
                        mx: 3,
                        mb: 3
                      }}>
                        <ImageIcon sx={{ fontSize: 40, color: '#A0AEC0', mb: 2 }} />
                        <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center' }}>
                          No images generated yet. Create content with images to see them here.
                        </Typography>
                      </Box>
                      )}
                    </ChartCard>
                  </Grid>

                  {/* Posts Activity */}
                  <Grid item xs={12} md={8}>
                    <ChartCard 
                      title="Posts Activity" 
                      sx={{ 
                        height: '100%',
                        borderRadius: 2,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                        border: '1px solid rgba(0, 0, 0, 0.05)'
                      }}
                    >
                      <Box sx={{ mb: 2, px: 3 }}>
                        <Typography variant="body2" color="text.secondary">
                          Your posting frequency over time
                        </Typography>
                      </Box>
                      <ResponsiveContainer width="100%" height={300}>
                        <AreaChart data={analytics.postsActivity}>
                          <defs>
                            <linearGradient id="colorPosts" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#4890fe" stopOpacity={0.1}/>
                              <stop offset="95%" stopColor="#4890fe" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                          <XAxis 
                            dataKey="date" 
                            tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { weekday: 'short' })}
                            tick={{ fill: '#4A5568', fontSize: 12 }}
                            axisLine={{ stroke: '#E2E8F0' }}
                            tickLine={{ stroke: '#E2E8F0' }}
                          />
                          <YAxis 
                            tick={{ fill: '#4A5568', fontSize: 12 }}
                            axisLine={{ stroke: '#E2E8F0' }}
                            tickLine={{ stroke: '#E2E8F0' }}
                          />
                          <Tooltip 
                            labelFormatter={(date) => new Date(date).toLocaleDateString()}
                            formatter={(value) => [`${value} posts`, 'Posts']}
                            contentStyle={{
                              backgroundColor: 'white',
                              borderRadius: '8px',
                              border: '1px solid rgba(0, 0, 0, 0.05)',
                              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)'
                            }}
                          />
                          <Area 
                            type="monotone" 
                            dataKey="count" 
                            stroke="#4890fe" 
                            fillOpacity={1} 
                            fill="url(#colorPosts)" 
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </ChartCard>
                  </Grid>

                  {/* Usage Insights */}
                  <Grid item xs={12}>
                    <ChartCard 
                      title="Usage Insights" 
                      sx={{ 
                        borderRadius: 2,
                        boxShadow: '0 4px 20px rgba(0, 0, 0, 0.05)',
                        border: '1px solid rgba(0, 0, 0, 0.05)'
                      }}
                    >
                      <Box sx={{ p: 3 }}>
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={4}>
                            <Box sx={{ p: 2, bgcolor: 'rgba(72, 144, 254, 0.05)', borderRadius: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <CalendarTodayIcon sx={{ color: '#4890fe', mr: 1 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2D3748' }}>
                                  Posting Patterns
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {postingScheduleData.sort((a, b) => b.posts - a.posts)[0]?.name || 'Monday'} is your most active posting day. 
                                Consider maintaining a consistent posting schedule to maximize engagement.
                              </Typography>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={12} md={4}>
                            <Box sx={{ p: 2, bgcolor: 'rgba(56, 161, 105, 0.05)', borderRadius: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <FormatQuoteIcon sx={{ color: '#38A169', mr: 1 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2D3748' }}>
                                  Content Strategy
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {contentDistributionData.sort((a, b) => b.value - a.value)[0]?.name || 'Image'} content makes up the largest portion of your posts.
                                Experiment with diverse content types to engage different audience segments.
                              </Typography>
                            </Box>
                          </Grid>
                          
                          <Grid item xs={12} md={4}>
                            <Box sx={{ p: 2, bgcolor: 'rgba(237, 137, 54, 0.05)', borderRadius: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                                <PeopleIcon sx={{ color: '#ED8936', mr: 1 }} />
                                <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#2D3748' }}>
                                  {hasUnlimitedCredits() ? 'Usage Summary' : 'Credit Efficiency'}
                                </Typography>
                              </Box>
                              <Typography variant="body2" color="text.secondary">
                                {hasUnlimitedCredits() ? 
                                  `You've created ${formatNumber(analytics?.totalPosts || 0)} posts and ${formatNumber(analytics?.totalImages || 0)} images with your ${userPlan} plan. You have unlimited credits available.` :
                                  `You've created ${formatNumber(analytics?.totalPosts || 0)} posts using ${formatNumber(analytics?.totalCreditsUsed || 0)} credits, 
                                  averaging ${((analytics?.totalCreditsUsed || 0) / Math.max(1, analytics?.totalPosts || 1)).toFixed(1)} credits per post.
                                  ${((analytics?.totalCreditsUsed || 0) / Math.max(1, analytics?.totalPosts || 1)) > 2 ? 
                                    'Consider using templates to reduce credit usage.' : 
                                    'Great job optimizing your credit usage!'}`
                                }
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>
                      </Box>
                    </ChartCard>
                  </Grid>
                </Grid>
              </Paper>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </>
  );
};

export default Analytics; 